import type { FC, ReactElement } from 'react';
import React, { useEffect, useState } from 'react';
import type { KeenSliderOptions } from 'keen-slider/react';
import { useKeenSlider } from 'keen-slider/react';
import UIicons from '@ui-kit';
import { createImageErrorHandler } from '@utils/onImageError';
import Arrow from '@components/Carousel/components/Arrow';
import icons from '@icons';
import { Picture } from '@ui';
import './index.scss';
import clsx from 'clsx';

const {
  playCircle: { black: playCircleBlackOutlined }
} = icons.Outline;
const { productCardErrorImg } = UIicons;

const onImgError = createImageErrorHandler(productCardErrorImg.x3);

interface ThumbsCarouselProps {
  list: ReactElement[];
  images: Array<{ thumb: string; type: string }>;
  currentSlide: number;
}

const ThumbsCarousel: FC<ThumbsCarouselProps> = ({
  list,
  images,
  currentSlide
}) => {
  const [loaded, setLoaded] = useState(false);
  const options: KeenSliderOptions = {
    rubberband: false,
    mode: 'free-snap',
    loop: images.length > 5,
    vertical: true,
    breakpoints: {
      '(min-width: 1280px)': {
        slides: {
          perView: 5,
          origin: 'auto',
          spacing: 12
        }
      }
    },
    created() {
      setLoaded(true);
    }
  };
  const [sliderRef, instanceRef] = useKeenSlider(options);
  useEffect(
    () => instanceRef.current?.moveToIdx(currentSlide),
    [instanceRef, currentSlide]
  );
  useEffect(() => {
    instanceRef.current?.update();
  }, [instanceRef, images]);
  return (
    <div className='pp-image-thumbs'>
      {!!images?.length && (
        <div ref={sliderRef} className='keen-slider'>
          {list}
        </div>
      )}
      {images.length > 5 && loaded && instanceRef && (
        <>
          <Arrow
            className='pp-image-thumbs__arrow'
            left
            height='26'
            onClick={e => {
              e.stopPropagation();
              instanceRef.current?.prev();
            }}
          />
          <Arrow
            className='pp-image-thumbs__arrow pp-image-thumbs__arrow--right'
            height='26'
            onClick={e => {
              e.stopPropagation();
              instanceRef.current?.next();
            }}
          />
        </>
      )}
    </div>
  );
};

interface PPThumbsProps {
  images: Array<{ thumb: string; type: string }>;
  changeSlide:
    | ((
        idx: number,
        absolute?: boolean,
        animation?: {
          duration?: number;
          easing?: (t: number) => number;
        }
      ) => void)
    | undefined;
  currentSlide: number;
  title: string;
  is_18plus?: boolean;
}

const PPThumbs: FC<PPThumbsProps> = ({
  images,
  changeSlide,
  currentSlide,
  title,
  is_18plus
}) => {
  const showCarousel = images.length > 5;
  const list = images.map((item, index) => (
    <div
      key={`thumb-${index}`}
      className={
        showCarousel ? 'keen-slider__slide' : 'pp-image-thumbs__thumb-wrapper'
      }
      onClick={() => {
        changeSlide?.(index);
      }}
      style={{
        maxWidth: '50px',
        minWidth: '50px',
        minHeight: '50px',
        maxHeight: '50px',
        height: '50px'
      }}
    >
      {item.type === 'video' && (
        <Picture
          pictureClassName='pp-image-thumbs__video-icon'
          icon={playCircleBlackOutlined}
          width='24'
          height='24'
        />
      )}
      <img
        draggable={false}
        onError={onImgError}
        className={clsx(
          `pp-image-thumbs__thumb`,
          index !== currentSlide && 'pp-image-thumbs__thumb--transparent',
          is_18plus && 'pp-image-thumbs__thumb--18plus'
        )}
        src={item.thumb || productCardErrorImg.x3}
        alt={`thumb-${title}-${index}}`}
      />
    </div>
  ));
  if (images.length > 5) {
    return (
      <ThumbsCarousel list={list} images={images} currentSlide={currentSlide} />
    );
  }
  return <div className='pp-image-thumbs pp-image-thumbs--list'>{list}</div>;
};

export default PPThumbs;
